/**
 * Incident Reporting Page
 * BuilderBel Construction Project Management System
 */

import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Eye,
  Inbox,
  Pencil,
  Plus,
  Shield,
  Trash2,
  X,
} from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';
import { SearchableDropdown } from '../components/forms';
import { IncidentDrawer } from '../components/incidents';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  ActionMenu,
  type ActionMenuItem,
  EmptyState,
  ModuleLayout,
  StatusBadge,
} from '../components/ui';
import { useToast } from '../contexts/ToastContext';
import { useCurrentProjectId } from '../hooks/useProjectRedux';
import {
  useCloseIncidentMutation,
  useDeleteIncidentMutation,
  useGetIncidentStatsQuery,
  useGetIncidentsQuery,
} from '../services/incidentApi';
import type { Incident, IncidentSeverity, IncidentStatus, IncidentType } from '../types/incident';

// ============================================================================
// Filter Options
// ============================================================================

const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'injury', label: 'Injury' },
  { value: 'illness', label: 'Illness' },
  { value: 'near_miss', label: 'Near Miss' },
  { value: 'property_damage', label: 'Property Damage' },
  { value: 'environmental', label: 'Environmental' },
  { value: 'security', label: 'Security' },
  { value: 'other', label: 'Other' },
];

const SEVERITY_OPTIONS = [
  { value: '', label: 'All Severities' },
  { value: 'minor', label: 'Minor' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'serious', label: 'Serious' },
  { value: 'severe', label: 'Severe' },
  { value: 'fatal', label: 'Fatal' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'reported', label: 'Reported' },
  { value: 'investigating', label: 'Investigating' },
  { value: 'pending_actions', label: 'Pending Actions' },
  { value: 'closed', label: 'Closed' },
];

const SORT_OPTIONS = [
  { value: 'incidentDate', label: 'Incident Date' },
  { value: 'createdAt', label: 'Reported Date' },
  { value: 'incidentNumber', label: 'Incident Number' },
  { value: 'severity', label: 'Severity' },
  { value: 'status', label: 'Status' },
];

// ============================================================================
// Helpers
// ============================================================================

const getSeverityBadgeClass = (severity: IncidentSeverity): string => {
  switch (severity) {
    case 'minor':
      return 'bg-green-100 text-green-700';
    case 'moderate':
      return 'bg-yellow-100 text-yellow-700';
    case 'serious':
      return 'bg-orange-100 text-orange-700';
    case 'severe':
      return 'bg-red-100 text-red-700';
    case 'fatal':
      return 'bg-gray-900 text-white';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const getStatusVariant = (
  status: IncidentStatus
): 'success' | 'warning' | 'error' | 'info' | 'neutral' => {
  switch (status) {
    case 'closed':
      return 'success';
    case 'investigating':
      return 'info';
    case 'pending_actions':
      return 'warning';
    case 'reported':
    default:
      return 'neutral';
  }
};

const getStatusLabel = (status: IncidentStatus): string => {
  const labels: Record<IncidentStatus, string> = {
    reported: 'Reported',
    investigating: 'Investigating',
    pending_actions: 'Pending Actions',
    closed: 'Closed',
  };
  return labels[status] || status;
};

const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatType = (type: IncidentType): string => {
  const labels: Record<IncidentType, string> = {
    injury: 'Injury',
    illness: 'Illness',
    near_miss: 'Near Miss',
    property_damage: 'Property Damage',
    environmental: 'Environmental',
    security: 'Security',
    other: 'Other',
  };
  return labels[type] || type;
};

// ============================================================================
// Page Component
// ============================================================================

const IncidentReporting: React.FC = () => {
  const projectId = useCurrentProjectId();
  const toast = useToast();

  // Filters & pagination
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState<IncidentType | ''>('');
  const [filterSeverity, setFilterSeverity] = useState<IncidentSeverity | ''>('');
  const [filterStatus, setFilterStatus] = useState<IncidentStatus | ''>('');
  const [sortBy, setSortBy] = useState('incidentDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const limit = 20;

  // Drawer state
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit' | 'view'>('create');

  // Row action menu state
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);

  // API calls
  const {
    data: incidentsResponse,
    isLoading,
    isFetching,
    refetch,
  } = useGetIncidentsQuery(
    {
      projectId: projectId || '',
      params: {
        page,
        limit,
        search: search || undefined,
        type: filterType || undefined,
        severity: filterSeverity || undefined,
        status: filterStatus || undefined,
        sortBy: sortBy as 'incidentDate' | 'createdAt' | 'incidentNumber' | 'severity' | 'status',
        sortOrder,
      },
    },
    { skip: !projectId }
  );

  const { data: statsResponse } = useGetIncidentStatsQuery(
    { projectId: projectId || '' },
    { skip: !projectId }
  );

  const [deleteIncident, { isLoading: isDeleting }] = useDeleteIncidentMutation();
  const [closeIncident] = useCloseIncidentMutation();

  // Computed data
  const incidents = useMemo(() => incidentsResponse?.data?.incidents || [], [incidentsResponse]);
  const pagination = incidentsResponse?.data?.pagination;
  const stats = statsResponse?.data;

  // Header stats
  const headerStats = useMemo(() => {
    if (!stats) return [];
    return [
      { title: 'Total', value: stats.total, variant: 'brand' as const },
      {
        title: 'Open',
        value: (stats.reported || 0) + (stats.investigating || 0) + (stats.pendingActions || 0),
        variant: 'warning' as const,
      },
      { title: 'OSHA Recordable', value: stats.oshaRecordable, variant: 'danger' as const },
      { title: 'Open Actions', value: stats.openActions, variant: 'info' as const },
    ];
  }, [stats]);

  // Handlers
  const handleCreate = useCallback(() => {
    setSelectedIncident(null);
    setDrawerMode('create');
    setIsDrawerOpen(true);
  }, []);

  const handleView = useCallback((incident: Incident) => {
    setSelectedIncident(incident);
    setDrawerMode('view');
    setIsDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((incident: Incident) => {
    setSelectedIncident(incident);
    setDrawerMode('edit');
    setIsDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setSelectedIncident(null);
  }, []);

  const handleDelete = useCallback(
    async (incident: Incident) => {
      if (!projectId) return;
      if (!window.confirm(`Delete incident ${incident.incidentNumber}? This cannot be undone.`)) {
        return;
      }
      try {
        await deleteIncident({ projectId, incidentId: incident._id }).unwrap();
        toast.success('Incident deleted');
      } catch {
        toast.error('Failed to delete incident');
      }
    },
    [projectId, deleteIncident, toast]
  );

  const handleClose = useCallback(
    async (incident: Incident) => {
      if (!projectId) return;
      try {
        await closeIncident({ projectId, incidentId: incident._id }).unwrap();
        toast.success('Incident closed');
        refetch();
      } catch (err: unknown) {
        const message =
          err &&
          typeof err === 'object' &&
          'data' in err &&
          typeof (err as { data?: { message?: string } }).data?.message === 'string'
            ? (err as { data: { message: string } }).data.message
            : 'Failed to close incident';
        toast.error(message);
      }
    },
    [projectId, closeIncident, toast, refetch]
  );

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  // Render filters
  const renderFilters = () => (
    <div className="flex items-center gap-2">
      <SearchableDropdown
        placeholder="Type"
        value={filterType}
        onChange={val => {
          setFilterType(val as IncidentType | '');
          setPage(1);
        }}
        options={TYPE_OPTIONS}
        searchable={false}
      />
      <SearchableDropdown
        placeholder="Severity"
        value={filterSeverity}
        onChange={val => {
          setFilterSeverity(val as IncidentSeverity | '');
          setPage(1);
        }}
        options={SEVERITY_OPTIONS}
        searchable={false}
      />
      <SearchableDropdown
        placeholder="Status"
        value={filterStatus}
        onChange={val => {
          setFilterStatus(val as IncidentStatus | '');
          setPage(1);
        }}
        options={STATUS_OPTIONS}
        searchable={false}
      />
    </div>
  );

  // Render pagination
  const renderPagination = () => {
    if (!pagination || pagination.pages <= 1) return null;

    const { page: currentPage, pages: totalPages, total } = pagination;
    const startItem = (currentPage - 1) * limit + 1;
    const endItem = Math.min(currentPage * limit, total);

    return (
      <div className="flex items-center justify-between">
        <div className="text-xs text-gray-500">
          Showing {startItem}-{endItem} of {total} results
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-xs text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  };

  // Render row actions
  const renderRowActions = (incident: Incident) => {
    const isMenuOpen = activeMenuId === incident._id;

    const menuItems: (ActionMenuItem | 'divider')[] = [
      {
        label: 'View Details',
        icon: <Eye className="h-3.5 w-3.5" />,
        onClick: () => handleView(incident),
      },
      {
        label: 'Edit',
        icon: <Pencil className="h-3.5 w-3.5" />,
        onClick: () => handleEdit(incident),
        hidden: incident.status === 'closed',
      },
      'divider',
      {
        label: 'Close Incident',
        icon: <Shield className="h-3.5 w-3.5" />,
        onClick: () => handleClose(incident),
        variant: 'success',
        hidden: incident.status === 'closed',
      },
      'divider',
      {
        label: 'Delete',
        icon: <Trash2 className="h-3.5 w-3.5" />,
        onClick: () => handleDelete(incident),
        variant: 'danger',
        disabled: isDeleting,
        hidden: incident.status !== 'reported',
      },
    ];

    return (
      <ActionMenu
        id={incident._id}
        items={menuItems}
        isOpen={isMenuOpen}
        onOpenChange={open => setActiveMenuId(open ? incident._id : null)}
      />
    );
  };

  // Loading state
  if (isLoading && incidents.length === 0) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  // No project selected
  if (!projectId) {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
        <EmptyState
          icon={Inbox}
          title="No Project Selected"
          description="Please select a project to view incident reports."
        />
      </div>
    );
  }

  const hasFilters = !!(search || filterType || filterSeverity || filterStatus);

  return (
    <ModuleLayout
      title="Incident Reporting"
      description="Report and track workplace incidents, investigations, and corrective actions"
      icon={<AlertTriangle className="h-4 w-4 text-brand-600" />}
      primaryAction={{
        label: 'Report Incident',
        onClick: handleCreate,
        icon: <Plus className="h-3 w-3 mr-1" />,
      }}
      stats={headerStats}
      searchValue={search}
      onSearchChange={val => {
        setSearch(val);
        setPage(1);
      }}
      filters={renderFilters()}
      sortOptions={SORT_OPTIONS}
      sortValue={sortBy}
      onSortChange={setSortBy}
      sortOrder={sortOrder}
      onSortOrderChange={setSortOrder}
      onRefresh={refetch}
      isLoading={isFetching}
      pagination={renderPagination()}
    >
      {incidents.length === 0 ? (
        <EmptyState
          icon={AlertTriangle}
          title="No incidents found"
          description={
            hasFilters
              ? "Try adjusting your filters to find what you're looking for."
              : 'Report incidents, track investigations, and implement corrective actions to maintain workplace safety.'
          }
          action={
            !hasFilters
              ? {
                  label: 'Report Incident',
                  onClick: handleCreate,
                }
              : undefined
          }
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 text-left">
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  #
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Severity
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Location
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider text-center">
                  OSHA
                </th>
                <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {incidents.map(incident => (
                <tr
                  key={incident._id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleView(incident)}
                >
                  {/* Incident Number */}
                  <td className="px-4 py-3">
                    <span className="text-sm font-medium text-gray-900">
                      {incident.incidentNumber}
                    </span>
                  </td>

                  {/* Title */}
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-900 truncate max-w-[200px] block">
                      {incident.title}
                    </span>
                  </td>

                  {/* Type */}
                  <td className="px-4 py-3">
                    <span className="text-xs text-gray-600">{formatType(incident.type)}</span>
                  </td>

                  {/* Severity — color-coded badge */}
                  <td className="px-4 py-3">
                    <span
                      className={`text-xs font-medium px-2 py-1 rounded-full capitalize ${getSeverityBadgeClass(incident.severity)}`}
                    >
                      {incident.severity}
                    </span>
                  </td>

                  {/* Status */}
                  <td className="px-4 py-3">
                    <StatusBadge
                      status={getStatusLabel(incident.status)}
                      variant={getStatusVariant(incident.status)}
                      size="sm"
                    />
                  </td>

                  {/* Date */}
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600">
                      {formatDate(incident.incidentDate)}
                    </span>
                  </td>

                  {/* Location */}
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600 truncate max-w-[150px] block">
                      {incident.location}
                    </span>
                  </td>

                  {/* OSHA Recordable */}
                  <td className="px-4 py-3 text-center">
                    {incident.oshaRecordable ? (
                      <span
                        title="OSHA Recordable"
                        className="inline-flex items-center justify-center w-5 h-5 bg-red-100 text-red-700 rounded-full"
                      >
                        <X className="h-3 w-3" />
                      </span>
                    ) : (
                      <span className="text-gray-300 text-xs">—</span>
                    )}
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    {renderRowActions(incident)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Incident Drawer */}
      <IncidentDrawer
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        incident={selectedIncident}
        mode={drawerMode}
        onSuccess={refetch}
      />
    </ModuleLayout>
  );
};

export default IncidentReporting;
